// pages/PricingPage.tsx - Interactive quote builder
import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check, ArrowRight, Calculator } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { getPath } from '../config/routes';
import { useSiteConfig } from '../hooks/useSiteConfig';

const PricingPage: React.FC = () => {
  const navigate = useNavigate();
  const { language, t } = useLanguage();
  const { config } = useSiteConfig();

  // Selection state
  const [selectedMaterial, setSelectedMaterial] = useState<string | null>(null);
  const [selectedSizeValue, setSelectedSizeValue] = useState<number>(13); // Size in inches
  const [selectedServices, setSelectedServices] = useState<string[]>([]);
  const [selectedUrgency, setSelectedUrgency] = useState<string | null>(null);
  const [isDuoprop, setIsDuoprop] = useState(false);
  const [selectedBladeCount, setSelectedBladeCount] = useState<number>(3); // Default 3 blades

  // Auto-select balancing when material is chosen (size and blades already have defaults)
  useEffect(() => {
    if (selectedMaterial && !selectedServices.includes('Tasapainotus') && !selectedServices.includes('Tarkastusmaksu')) {
      setSelectedServices(prev => [...prev, 'Tasapainotus']);
    }
  }, [selectedMaterial]);

  // Dynamic prices from config or defaults
  const basePrices = useMemo(() => [
    { material: 'Titaani', materialKey: 'pricing.material.titanium', basePrice: config?.prices?.materials?.titanium ?? 200, descKey: 'pricing.material.titanium.desc' },
    { material: 'Pronssi', materialKey: 'pricing.material.bronze', basePrice: config?.prices?.materials?.bronze ?? 130, descKey: 'pricing.material.bronze.desc', popular: true },
    { material: 'Ruostumaton teräs', materialKey: 'pricing.material.stainless', basePrice: config?.prices?.materials?.stainless ?? 110, descKey: 'pricing.material.stainless.desc', popular: true },
    { material: 'Alumiini', materialKey: 'pricing.material.aluminum', basePrice: config?.prices?.materials?.aluminum ?? 95, descKey: 'pricing.material.aluminum.desc', popular: true }
  ], [config]);

  // Size tiers with boundaries - using config multipliers
  const sizeTiers = useMemo(() => [
    { min: 8, max: 13, multiplier: config?.prices?.size_multipliers?.['8-12.75'] ?? 1.0, label: '8-13"' },
    { min: 13, max: 18, multiplier: config?.prices?.size_multipliers?.['13-17.75'] ?? 1.3, label: '13-18"' },
    { min: 18, max: 23, multiplier: config?.prices?.size_multipliers?.['18-22.75'] ?? 1.6, label: '18-23"' },
    { min: 23, max: 28, multiplier: config?.prices?.size_multipliers?.['23-27.75'] ?? 2.0, label: '23-28"' },
    { min: 28, max: 33, multiplier: config?.prices?.size_multipliers?.['28+'] ?? 2.5, label: '28"+' }
  ], [config]);

  // Slider config
  const sliderMin = 8;
  const sliderMax = 33;
  const sliderStep = 0.25;
  const tickMarks = [8, 13, 18, 23, 28, 33];

  // Get size tier based on value
  const getSizeTier = (value: number) => {
    for (const tier of sizeTiers) {
      if (value >= tier.min && value < tier.max) {
        return tier;
      }
    }
    return sizeTiers[sizeTiers.length - 1]; // Return last tier for max values
  };

  const currentSizeTier = getSizeTier(selectedSizeValue);

  // Services in display order (rows of 2, last one centered) - using config prices
  // scalesWithWork: true = affected by size/blade multipliers (physical work on propeller)
  // scalesWithWork: false = fixed price from admin panel
  // isStarting: true = show "from/alkaen" prefix
  const additionalServices = useMemo(() => [
    { service: 'Tasapainotus', serviceKey: 'pricing.services.balancing', price: config?.prices?.services?.balancing ?? 50, descKey: 'pricing.services.balancing.desc', isStarting: true, scalesWithWork: true, doubleForDuoprop: true },
    { service: 'Kiillotus', serviceKey: 'pricing.services.polishing', price: config?.prices?.services?.polishing ?? 40, descKey: 'pricing.services.polishing.desc', isStarting: true, scalesWithWork: true, doubleForDuoprop: true },
    { service: 'Pinnoitus', serviceKey: 'pricing.services.coating', price: config?.prices?.services?.coating ?? 20, descKey: 'pricing.services.coating.desc', isStarting: true, scalesWithWork: true, doubleForDuoprop: true },
    { service: 'Holkin vaihto', serviceKey: 'pricing.services.bushing', price: config?.prices?.services?.bushing ?? 50, descKey: 'pricing.services.bushing.desc', scalesWithWork: false, doubleForDuoprop: true },
    { service: 'Dokumentointi', serviceKey: 'pricing.services.documentation', price: config?.prices?.services?.documentation ?? 30, descKey: 'pricing.services.documentation.desc', scalesWithWork: false, doubleForDuoprop: false },
    { service: 'Toimitus', serviceKey: 'pricing.services.shipping', price: config?.prices?.services?.shipping ?? 15, descKey: 'pricing.services.shipping.desc', isStarting: true, scalesWithWork: false, doubleForDuoprop: false }
  ], [config]);

  // Tarkastusmaksu is separate (clears other selections when chosen)
  const tarkastusmaksu = useMemo(() => ({
    service: 'Tarkastusmaksu',
    serviceKey: 'pricing.services.inspection',
    price: config?.prices?.services?.inspection ?? 30,
    descKey: 'pricing.services.inspection.desc'
  }), [config]);

  // Urgency multipliers from config
  const urgencyPricing = useMemo(() => [
    { type: 'Normaali', typeKey: 'pricing.urgency.normal', timeKey: 'pricing.urgency.normal.time', multiplier: config?.prices?.urgency?.normal ?? 1.0, color: 'green' },
    { type: 'Kiireellinen', typeKey: 'pricing.urgency.urgent', timeKey: 'pricing.urgency.urgent.time', multiplier: config?.prices?.urgency?.urgent ?? 1.35, color: 'yellow' },
    { type: 'Erittäin kiireellinen', typeKey: 'pricing.urgency.veryUrgent', timeKey: 'pricing.urgency.veryUrgent.time', multiplier: config?.prices?.urgency?.very_urgent ?? 1.6, color: 'orange' },
    { type: 'Hätätyö', typeKey: 'pricing.urgency.emergency', timeKey: 'pricing.urgency.emergency.time', multiplier: config?.prices?.urgency?.emergency ?? 2.0, color: 'red' }
  ], [config]);

  // Blade count factors from config
  const bladeFactors = useMemo(() => [
    { blades: 2, factor: config?.prices?.blade_factors?.['2'] ?? 0.75, labelKey: 'pricing.blades.2' },
    { blades: 3, factor: config?.prices?.blade_factors?.['3'] ?? 1.0, labelKey: 'pricing.blades.3' },
    { blades: 4, factor: config?.prices?.blade_factors?.['4'] ?? 1.25, labelKey: 'pricing.blades.4' }
  ], [config]);

  // Get blade factor for selected blade count
  const getBladeFactor = (bladeCount: number): number => {
    const bladeFactor = bladeFactors.find(b => b.blades === bladeCount);
    return bladeFactor ? bladeFactor.factor : 1.0;
  };

  // Toggle service selection
  const toggleService = (service: string) => {
    if (service === 'Tarkastusmaksu') {
      // Tarkastusmaksu clears all other services AND material/size selections
      if (selectedServices.includes('Tarkastusmaksu')) {
        setSelectedServices([]);
      } else {
        setSelectedServices(['Tarkastusmaksu']);
        setSelectedMaterial(null);
        setSelectedUrgency(null);
      }
    } else {
      // Remove Tarkastusmaksu if selecting other services
      setSelectedServices(prev => {
        const withoutTarkastus = prev.filter(s => s !== 'Tarkastusmaksu');
        return withoutTarkastus.includes(service)
          ? withoutTarkastus.filter(s => s !== service)
          : [...withoutTarkastus, service];
      });
    }
  };

  // Calculate total price
  const calculatePrice = () => {
    const urgency = urgencyPricing.find(u => u.type === selectedUrgency) || urgencyPricing[0];
    const bladeFactor = getBladeFactor(selectedBladeCount);

    // Special case: Tarkastusmaksu only (30€, or 60€ if duoprop)
    if (selectedServices.includes('Tarkastusmaksu')) {
      const tarkastusDuopropMultiplier = isDuoprop ? (config?.prices?.duoprop_multiplier ?? 2) : 1;
      const tarkastusPrice = tarkastusmaksu.price * tarkastusDuopropMultiplier;
      return {
        baseRepairCost: '0.00',
        urgencyMultiplier: 1,
        repairCost: '0.00',
        servicesCost: tarkastusPrice.toFixed(2),
        total: tarkastusPrice.toFixed(2),
        isTarkastusOnly: true
      };
    }

    // Normal calculation requires material
    if (!selectedMaterial) return null;

    const material = basePrices.find(p => p.material === selectedMaterial);
    const sizeTier = getSizeTier(selectedSizeValue);

    if (!material) return null;

    // Calculate scaling services sum (balancing, polishing, coating)
    const scalingServicesSum = selectedServices.reduce((total, serviceName) => {
      const service = additionalServices.find(s => s.service === serviceName);
      if (service && service.scalesWithWork) {
        return total + service.price;
      }
      return total;
    }, 0);

    // Calculate fixed services sum (bushing, documentation, shipping)
    const configDuopropMultiplier = config?.prices?.duoprop_multiplier ?? 2;
    const fixedServicesSum = selectedServices.reduce((total, serviceName) => {
      const service = additionalServices.find(s => s.service === serviceName);
      if (service && !service.scalesWithWork) {
        const serviceMultiplier = (isDuoprop && service.doubleForDuoprop) ? configDuopropMultiplier : 1;
        return total + (service.price * serviceMultiplier);
      }
      return total;
    }, 0);

    // Base = Material + Scaling Services
    const baseSum = material.basePrice + scalingServicesSum;

    // Apply all multipliers: Size × Blades × Urgency × Duoprop
    const duopropMultiplier = isDuoprop ? (config?.prices?.duoprop_multiplier ?? 2) : 1;
    const multipliedTotal = baseSum * sizeTier.multiplier * bladeFactor * urgency.multiplier * duopropMultiplier;

    // Total = multiplied base + fixed services
    const total = multipliedTotal + fixedServicesSum;

    return {
      baseRepairCost: baseSum.toFixed(2),
      urgencyMultiplier: urgency.multiplier,
      repairCost: multipliedTotal.toFixed(2),
      servicesCost: fixedServicesSum.toFixed(2),
      total: total.toFixed(2),
      isTarkastusOnly: false
    };
  };

  const priceBreakdown = calculatePrice();

  // Transfer to contact form
  const handleTransferToContact = () => {
    const queryParams = new URLSearchParams();
    if (selectedMaterial) queryParams.set('material', selectedMaterial);
    queryParams.set('size', `${selectedSizeValue}"`);
    if (selectedUrgency) queryParams.set('urgency', selectedUrgency);
    if (isDuoprop) queryParams.set('duoprop', 'true');
    queryParams.set('blades', selectedBladeCount.toString());
    if (selectedServices.length > 0) queryParams.set('services', selectedServices.join(','));
    if (priceBreakdown) queryParams.set('estimate', priceBreakdown.total);

    navigate(`${getPath('contact', language)}?${queryParams.toString()}`);
  };

  return (
    <div className="pt-20 min-h-screen bg-white">
      {/* Hero */}
      <div className="bg-gradient-to-br from-cyan-900 via-blue-900 to-teal-900 py-20 relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="text-center text-white">
            <h1 className="text-4xl md:text-6xl font-bold mb-6">{t('pricing.title')}</h1>
            <p className="text-xl text-cyan-100 max-w-3xl mx-auto">
              {t('pricing.subtitle')}
            </p>
          </div>
        </div>
        <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-gray-50 via-gray-50/50 to-transparent pointer-events-none" />
      </div>

      {/* Material Selection */}
      <div className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-4">{t('pricing.material')}</h2>
          <p className="text-center text-gray-500 text-sm mb-8">
            {t('pricing.material.note')}
          </p>

          {/* Materials grid: 2x2 layout */}
          <div className="grid md:grid-cols-2 gap-4 max-w-4xl mx-auto">
            {basePrices.map((item) => (
              <button
                key={item.material}
                onClick={() => setSelectedMaterial(item.material)}
                className={`relative bg-white rounded-2xl p-6 shadow-lg transition-all duration-300 text-left ${
                  selectedMaterial === item.material
                    ? 'ring-4 ring-cyan-500 shadow-xl scale-[1.02]'
                    : 'hover:shadow-xl hover:scale-[1.01]'
                } ${item.popular ? 'ring-2 ring-cyan-200' : ''}`}
              >
                {selectedMaterial === item.material && (
                  <div className="absolute top-4 right-4">
                    <div className="bg-cyan-500 rounded-full p-1">
                      <Check className="w-4 h-4 text-white" />
                    </div>
                  </div>
                )}
                {item.popular && (
                  <span className="absolute -top-2 left-4 bg-gradient-to-r from-cyan-500 to-blue-500 text-white px-3 py-0.5 rounded-full text-xs font-semibold">
                    {t('pricing.common')}
                  </span>
                )}
                <h3 className="text-xl font-bold text-gray-900 mb-1">{t(item.materialKey)}</h3>
                <div className="text-3xl font-bold text-cyan-600 mb-2">{item.basePrice}€</div>
                <p className="text-sm text-gray-600">{t(item.descKey)}</p>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Size Selection */}
      <div className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-4">{t('pricing.size')}</h2>

          {/* Current size display */}
          <div className="text-center mb-8">
            <span className="text-4xl font-bold text-cyan-600">{selectedSizeValue}"</span>
          </div>

          {/* Slider container */}
          <div className="max-w-3xl mx-auto px-4">
            {/* Tick marks */}
            <div className="relative h-8 mb-2">
              {tickMarks.map((tick) => {
                const position = ((tick - sliderMin) / (sliderMax - sliderMin)) * 100;
                const isCurrentTier = tick === currentSizeTier.min || (tick === 28 && selectedSizeValue >= 28);
                return (
                  <div
                    key={tick}
                    className="absolute flex flex-col items-center"
                    style={{ left: `${position}%`, transform: 'translateX(-50%)' }}
                  >
                    <div className={`w-0.5 h-3 ${isCurrentTier ? 'bg-cyan-500' : 'bg-gray-300'}`} />
                    <span className={`text-xs mt-1 ${isCurrentTier ? 'text-cyan-600 font-bold' : 'text-gray-400'}`}>
                      {tick}"
                    </span>
                  </div>
                );
              })}
            </div>

            {/* Slider */}
            <div className="relative">
              <input
                type="range"
                min={sliderMin}
                max={sliderMax}
                step={sliderStep}
                value={selectedSizeValue}
                onChange={(e) => setSelectedSizeValue(parseFloat(e.target.value))}
                className="w-full h-3 bg-gray-200 rounded-lg appearance-none cursor-pointer slider-thumb"
                style={{
                  background: `linear-gradient(to right, #06b6d4 0%, #06b6d4 ${((selectedSizeValue - sliderMin) / (sliderMax - sliderMin)) * 100}%, #e5e7eb ${((selectedSizeValue - sliderMin) / (sliderMax - sliderMin)) * 100}%, #e5e7eb 100%)`
                }}
              />
            </div>

          </div>

          {/* Duoprop Option */}
          <div className="mt-8 flex justify-center">
            <button
              onClick={() => setIsDuoprop(!isDuoprop)}
              className={`flex items-center gap-3 px-6 py-4 rounded-xl border-2 transition-all duration-300 ${
                isDuoprop
                  ? 'border-cyan-500 bg-cyan-50 shadow-lg'
                  : 'border-gray-200 bg-white hover:border-cyan-300'
              }`}
            >
              <div className={`w-6 h-6 rounded border-2 flex items-center justify-center transition-colors ${
                isDuoprop ? 'bg-cyan-500 border-cyan-500' : 'border-gray-300'
              }`}>
                {isDuoprop && <Check className="w-4 h-4 text-white" />}
              </div>
              <div className="text-left">
                <div className="font-bold text-gray-900">{t('pricing.duoprop')}</div>
              </div>
            </button>
          </div>

          {/* Blade Count Selection */}
          <div className="mt-8">
            <h3 className="text-xl font-bold text-center mb-4">{t('pricing.blades')}</h3>
            <div className="flex justify-center gap-2">
              {bladeFactors.map((item) => (
                <button
                  key={item.blades}
                  onClick={() => setSelectedBladeCount(item.blades)}
                  className={`relative px-4 py-3 rounded-xl border-2 transition-all duration-300 min-w-[60px] ${
                    selectedBladeCount === item.blades
                      ? 'border-cyan-500 bg-cyan-50 shadow-lg'
                      : 'border-gray-200 bg-white hover:border-cyan-300'
                  }`}
                >
                  {selectedBladeCount === item.blades && (
                    <div className="absolute -top-2 -right-2">
                      <div className="bg-cyan-500 rounded-full p-1">
                        <Check className="w-3 h-3 text-white" />
                      </div>
                    </div>
                  )}
                  <div className="text-lg font-bold text-gray-900">{t(item.labelKey)}</div>
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Additional Services */}
      <div className="py-16 bg-gray-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-4">{t('pricing.services')}</h2>
          {isDuoprop && (
            <p className="text-center text-cyan-600 text-sm mb-8">
              {t('pricing.services.duopropNote')}
            </p>
          )}
          {!isDuoprop && <div className="mb-8" />}

          {/* Services grid */}
          <div className="grid md:grid-cols-2 gap-4 mb-4">
            {additionalServices.map((item) => (
              <button
                key={item.service}
                onClick={() => toggleService(item.service)}
                className={`relative bg-white rounded-xl p-5 shadow-md transition-all duration-300 text-left border-2 ${
                  selectedServices.includes(item.service)
                    ? 'border-cyan-500 bg-cyan-50 shadow-lg'
                    : 'border-transparent hover:shadow-lg'
                }`}
              >
                <div className="flex items-start gap-3">
                  <div className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 mt-0.5 transition-colors ${
                    selectedServices.includes(item.service) ? 'bg-cyan-500 border-cyan-500' : 'border-gray-300'
                  }`}>
                    {selectedServices.includes(item.service) && <Check className="w-3 h-3 text-white" />}
                  </div>
                  <div className="flex-1">
                    <div className="flex justify-between items-start">
                      <div className="flex items-center gap-2">
                        <h3 className="font-bold text-gray-900">{t(item.serviceKey)}</h3>
                        {isDuoprop && item.doubleForDuoprop && (
                          <span className="text-xs bg-cyan-100 text-cyan-700 px-2 py-0.5 rounded">x2</span>
                        )}
                      </div>
                      <span className="text-lg font-bold text-cyan-600 ml-2">
                        {item.isStarting ? `${t('pricing.starting')} ${item.price}€` : `${item.price}€`}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600">{t(item.descKey)}</p>
                  </div>
                </div>
              </button>
            ))}
          </div>

          {/* Tarkastusmaksu centered */}
          <div className="flex justify-center">
            <button
              onClick={() => toggleService('Tarkastusmaksu')}
              className={`w-full md:w-1/2 bg-white rounded-xl p-5 shadow-md transition-all duration-300 text-left border-2 ${
                selectedServices.includes('Tarkastusmaksu')
                  ? 'border-amber-500 bg-amber-50 shadow-lg'
                  : 'border-transparent hover:shadow-lg'
              }`}
            >
              <div className="flex items-start gap-3">
                <div className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 mt-0.5 transition-colors ${
                  selectedServices.includes('Tarkastusmaksu') ? 'bg-amber-500 border-amber-500' : 'border-gray-300'
                }`}>
                  {selectedServices.includes('Tarkastusmaksu') && <Check className="w-3 h-3 text-white" />}
                </div>
                <div className="flex-1">
                  <div className="flex justify-between items-start">
                    <h3 className="font-bold text-gray-900">{t(tarkastusmaksu.serviceKey)}</h3>
                    <span className="text-lg font-bold text-amber-600 ml-2">{tarkastusmaksu.price}€</span>
                  </div>
                  <p className="text-sm text-gray-600">{t(tarkastusmaksu.descKey)}</p>
                </div>
              </div>
            </button>
          </div>
        </div>
      </div>

      {/* Urgency Selection */}
      <div className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-8">{t('pricing.urgency')}</h2>

          <div className="grid md:grid-cols-2 gap-4">
            {urgencyPricing.map((item) => {
              const colorClasses = {
                green: { border: 'border-green-400', bg: 'bg-green-50', selectedBg: 'bg-green-100' },
                yellow: { border: 'border-yellow-400', bg: 'bg-yellow-50', selectedBg: 'bg-yellow-100' },
                orange: { border: 'border-orange-400', bg: 'bg-orange-50', selectedBg: 'bg-orange-100' },
                red: { border: 'border-red-400', bg: 'bg-red-50', selectedBg: 'bg-red-100' }
              };
              const colors = colorClasses[item.color as keyof typeof colorClasses];
              const isSelected = selectedUrgency === item.type;

              return (
                <button
                  key={item.type}
                  onClick={() => setSelectedUrgency(item.type)}
                  className={`relative rounded-xl p-5 transition-all duration-300 text-left border-2 ${colors.border} ${
                    isSelected ? `${colors.selectedBg} shadow-lg ring-2 ring-offset-2 ring-cyan-500` : `${colors.bg} hover:shadow-lg`
                  }`}
                >
                  {isSelected && (
                    <div className="absolute top-3 right-3">
                      <div className="bg-cyan-500 rounded-full p-1">
                        <Check className="w-4 h-4 text-white" />
                      </div>
                    </div>
                  )}
                  <div className="flex justify-between items-start">
                    <div>
                      <h3 className="font-bold text-lg text-gray-900">{t(item.typeKey)}</h3>
                      <p className="text-sm text-gray-600">{t(item.timeKey)}</p>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Price Result */}
      <div className="py-16 bg-gray-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-8">{t('pricing.estimate')}</h2>

          <div className="bg-white rounded-2xl shadow-xl p-8">
            {priceBreakdown ? (
              <>
                <div className="mb-6 text-center">
                  {/* Tarkastusmaksu only */}
                  {priceBreakdown.isTarkastusOnly ? (
                    <p className="text-lg font-medium text-gray-700">
                      {t('pricing.services.inspection')}{isDuoprop && ' (Duoprop)'}
                    </p>
                  ) : (
                    <>
                      <p className="text-lg font-medium text-gray-700">
                        {selectedMaterial}, {selectedSizeValue}", {selectedBladeCount} {t('pricing.blades.label')}
                        {isDuoprop && ' (Duoprop)'}
                      </p>
                      {selectedServices.length > 0 && (
                        <p className="text-base text-cyan-600 mt-2">
                          + {selectedServices.map(s => {
                            const svc = additionalServices.find(a => a.service === s);
                            return svc ? t(svc.serviceKey) : s;
                          }).join(', ')}
                        </p>
                      )}
                    </>
                  )}
                </div>

                <div className={`rounded-xl p-6 text-center ${priceBreakdown.isTarkastusOnly ? 'bg-amber-50' : 'bg-cyan-50'}`}>
                  <p className="text-sm text-gray-600 mb-2">
                    {priceBreakdown.isTarkastusOnly ? t('pricing.services.inspection') : t('pricing.estimate.total')}
                  </p>
                  <p className={`text-5xl font-bold ${priceBreakdown.isTarkastusOnly ? 'text-amber-600' : 'text-cyan-600'}`}>
                    {priceBreakdown.total}€
                  </p>
                  <p className="text-xs text-gray-500 mt-2">
                    {priceBreakdown.isTarkastusOnly
                      ? t('pricing.estimate.inspectionVat')
                      : t('pricing.estimate.vat')
                    }
                  </p>
                </div>

                <button
                  onClick={handleTransferToContact}
                  className="w-full mt-6 bg-gradient-to-r from-cyan-600 to-blue-600 text-white px-8 py-4 rounded-xl font-semibold hover:shadow-xl transition-all flex items-center justify-center gap-2"
                >
                  {t('pricing.sendQuote')}
                  <ArrowRight className="w-5 h-5" />
                </button>
              </>
            ) : (
              <div className="text-center py-8">
                <Calculator className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500 text-lg">{t('pricing.estimate.select')}</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* CTA */}
      <div className="py-16 bg-gradient-to-r from-cyan-600 to-blue-600">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            {t('pricing.help.title')}
          </h2>
          <p className="text-xl text-cyan-100 mb-8">
            {t('pricing.help.subtitle')}
          </p>
          <a
            href="tel:+358400416466"
            className="inline-flex items-center gap-2 border-2 border-white text-white px-8 py-4 rounded-full font-semibold hover:bg-white hover:text-cyan-600 transition-all"
          >
            {t('pricing.help.call')}: +358 400 416 466
          </a>
        </div>
      </div>
    </div>
  );
};

export default PricingPage;
